import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhotoIcon } from '@heroicons/react/24/solid';
import axios from 'axios';

const ProfileUpdate = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [bio, setBio] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { username } = useParams();

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await fetch(`https://happyu.onrender.com/${username}/getuserdetails`);
        if (response.status === 200) {
          const data = await response.json();
          const { name, email, gender, age, bio, profilePicture } = data;
          setName(name);
          setEmail(email);
          setGender(gender);
          setAge(age);
          setBio(bio);
          setProfilePicture(profilePicture);
        } else {
          setError('Failed to fetch user details');
        }
      } catch (error) {
        setError('Failed to fetch user details');
      }
    };
    fetchUserDetails();
  }, [username]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'newPassword':
        setNewPassword(value);
        break;
      case 'name':
        setName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'gender':
        setGender(value);
        break;
      case 'age':
        setAge(value);
        break;
      case 'bio':
        setBio(value);
        break;
      default:
        break;
    }
  };

  const handleFileChange = (e) => setProfilePicture(e.target.files[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      if (newPassword) formData.append('password', newPassword);
      formData.append('name', name);
      formData.append('email', email);
      formData.append('gender', gender);
      formData.append('age', age);
      formData.append('bio', bio);
      if (profilePicture) formData.append('profilePicture', profilePicture);

      const response = await axios.patch(
        `https://happyu.onrender.com/${username}/update-user`,
        formData,
        { headers: { 'Content-Type': 'application/json'} }
      );
      if (response.status === 200) {
        navigate(`/${username}/profile`);
      } else {
        setError('Failed to update profile');
      }
    } catch (error) {
      setError('Failed to update profile');
    }
  };

  const handleCancel = () => navigate(`/${username}/profile`);

  return (
    <div className="min-h-screen flex items-center justify-center py-10 px-2 bg-gradient-to-r from-indigo-50 via-teal-50 to-white">
      <div className="max-w-xl w-full bg-white/90 backdrop-blur rounded-2xl shadow-2xl border border-teal-100 px-8 py-10">
        <h2 className="text-3xl font-bold text-indigo-700 mb-6">Update Profile</h2>
        <form onSubmit={handleSubmit} className="space-y-7">
          <div>
            <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-1">Username</label>
            <input
              type="text"
              name="username"
              id="username"
              value={username}
              disabled
              className="block w-full rounded-md border-0 py-2 px-3 text-gray-400 shadow ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-indigo-500 bg-gray-50"
            />
          </div>
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="name"
              id="name"
              value={name}
              onChange={handleChange}
              className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
            <input
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={handleChange}
              className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              name="newPassword"
              id="newPassword"
              value={newPassword}
              onChange={handleChange}
              className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="age" className="block text-sm font-semibold text-gray-700 mb-1">Age</label>
            <input
              type="number"
              name="age"
              id="age"
              value={age}
              onChange={handleChange}
              className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="gender" className="block text-sm font-semibold text-gray-700 mb-1">Gender</label>
            <select
              id="gender"
              name="gender"
              value={gender}
              onChange={handleChange}
              className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Non Binary">Non Binary</option>
            </select>
          </div>
          <div>
            <label htmlFor="bio" className="block text-sm font-semibold text-gray-700 mb-1">Bio</label>
            <textarea
              id="bio"
              name="bio"
              rows={3}
              value={bio}
              onChange={handleChange}
              className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-2 text-sm text-gray-500">Write a few sentences about yourself.</p>
          </div>
          
          <div className="mt-6 flex justify-end gap-4">
            <button
              type="button"
              className="text-base font-semibold text-gray-700 px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200 transition"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-6 py-2 text-base font-bold text-white shadow hover:bg-teal-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
            >
              Save
            </button>
          </div>
          {error && (
            <p className="mt-4 text-sm text-red-600">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ProfileUpdate;
